import * as React from "react";
import { trackAction } from "../../../lib/usage";
import { Link, useLocation, useNavigate } from "react-router";
import { ChevronDown, ChevronUp, Sparkles, X } from "lucide-react";
import { Button, cn } from "@cm/ui";
import { api } from "../../../lib/api";
import { DelegateDialog, openAssistant, ProgressBar, StepIcon, useGuide, useGuideRefresh, type GuideStep } from "./guide-shared";

/** Páginas em que cada alvo destacado aparece (para o guia saber se o cliente está na tela certa). */
const TARGET_PAGES: Record<string, string[]> = {
  "meta-connect": ["/app/integrations/meta"],
  "meta-accounts": ["/app/integrations/meta"],
  "meta-dataset": ["/app/integrations/meta"],
  "integrations-new": ["/app/integrations"],
  "integration-form": ["/app/integrations"],
  "channels-discover": ["/app/integrations/"],
  "channel-enable": ["/app/integrations/"],
  "pipeline-sync": ["/app/integrations/"],
  "pipeline-milestones": ["/app/integrations/", "/app/pipelines"],
  "automation-template": ["/app/automations"],
  "form-configure": ["/app/forms"],
  "site-add": ["/app/tracking/sites"],
  "site-copy-code": ["/app/tracking/sites/"],
};

function onStepPage(step: GuideStep, path: string) {
  if (path.startsWith(step.href) && step.href !== "/app/guide") return true;
  return step.targets.some((t) => (TARGET_PAGES[t.id] ?? []).some((p) => path.startsWith(p)));
}

/** Destaque do botão real: anel posicionado sobre o elemento `[data-guide=id]` (não bloqueia o clique). */
function Highlight({ ids, onFound }: { ids: string[]; onFound: (id: string | null) => void }) {
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const key = ids.join(",");
  React.useEffect(() => {
    let scrolled: string | null = null;
    const tick = () => {
      let found: HTMLElement | null = null;
      let foundId: string | null = null;
      for (const id of ids) {
        const el = document.querySelector<HTMLElement>(`[data-guide="${id}"]`);
        if (el && el.offsetParent) {
          found = el;
          foundId = id;
          break;
        }
      }
      onFound(foundId);
      if (!found) return setRect(null);
      if (scrolled !== foundId) {
        found.scrollIntoView({ block: "center", behavior: "smooth" });
        scrolled = foundId;
      }
      setRect(found.getBoundingClientRect());
    };
    tick();
    const t = window.setInterval(tick, 400);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!rect) return null;
  const pad = 6;
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed z-30 rounded-lg ring-4 ring-brand ring-offset-2 ring-offset-surface motion-safe:animate-pulse"
      style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }}
    />
  );
}

/**
 * Guia na tela (V2-11): cartão flutuante com o passo atual, o botão real destacado, "Não tenho esse acesso",
 * pular e perguntar à IA. Confere sozinho (consulta o estado a cada 5 s) e avança quando o sistema vê o passo pronto.
 */
export function GuideOverlay() {
  const loc = useLocation();
  const nav = useNavigate();
  const refresh = useGuideRefresh();
  const onGuidePage = loc.pathname.startsWith("/app/guide");
  const q = useGuide({ poll: onGuidePage ? false : 5000 });
  const [collapsed, setCollapsed] = React.useState(false);
  const [delegating, setDelegating] = React.useState<GuideStep | null>(null);
  const [justDone, setJustDone] = React.useState<string | null>(null);
  const [targetId, setTargetId] = React.useState<string | null>(null);
  const prev = React.useRef<string | null>(null);
  const g = q.data;
  const step = g?.steps.find((s) => s.key === g.current_step) ?? null;
  React.useEffect(() => {
    if (!g) return;
    if (prev.current && prev.current !== g.current_step) {
      const was = g.steps.find((s) => s.key === prev.current);
      if (was?.status === "done") setJustDone(was.title);
    }
    prev.current = g.current_step;
  }, [g]);
  if (!g || !g.started || g.status !== "active" || onGuidePage || !step) return null;
  const here = onStepPage(step, loc.pathname);
  const act = async (path: string) => refresh(await api.post(path));

  return (
    <>
      {!collapsed && here && <Highlight ids={step.targets.map((t) => t.id)} onFound={setTargetId} />}
      <aside
        aria-label="Guia de configuração"
        className={cn("fixed bottom-4 right-4 z-30 w-[calc(100vw-2rem)] max-w-sm rounded-xl border border-border bg-surface shadow-2xl", collapsed && "w-auto")}
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <Sparkles className="h-4 w-4 text-brand" aria-hidden />
          <p className="min-w-0 flex-1 truncate text-sm font-semibold">{collapsed ? `Guia · ${g.progress.done}/${g.progress.total}` : "Guia de configuração"}</p>
          <button type="button" className="rounded p-1 text-muted hover:bg-surface-muted" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? "Abrir guia" : "Minimizar guia"}>
            {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {!collapsed && (
            <button type="button" className="rounded p-1 text-muted hover:bg-surface-muted" onClick={() => act("/api/app/guide/dismiss")} aria-label="Fechar guia" title="Fechar guia (você pode retomar em Configurações → Guia de configuração)">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {!collapsed && (
          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto px-4 py-3 text-sm">
            <ProgressBar done={g.progress.done} total={g.progress.total} label={`~${g.progress.minutes_left} min`} />
            {justDone && (
              <p className="flex items-center gap-2 rounded-md bg-success-bg px-3 py-2 text-success" role="status">
                <StepIcon status="done" className="h-4 w-4" /> {justDone}: pronto!
              </p>
            )}
            <div>
              <p className="text-xs text-muted">{step.path.length ? step.path.join(" → ") : "Próximo passo"}</p>
              <p className="font-semibold">{step.title}</p>
            </div>
            {step.status === "delegated" && step.delegated_to && <p className="rounded-md bg-info-bg px-3 py-2 text-info">Enviado para {step.delegated_to.name}. Você será avisado quando ficar pronto.</p>}
            {here ? (
              <ol className="flex flex-col gap-2">
                {step.targets.map((t, i) => (
                  <li key={t.id} className={cn("rounded-md border px-3 py-2", targetId === t.id ? "border-brand bg-brand-50/60" : "border-border")}>
                    <p><span className="font-medium">{i + 1}.</span> {t.text}</p>
                    {t.why && <p className="mt-0.5 text-xs text-muted">Por quê: {t.why}</p>}
                  </li>
                ))}
              </ol>
            ) : (
              <Button onClick={() => { trackAction("guide.overlay_go"); nav(step.href); }}>Ir para {step.path.at(-1) ?? "a tela"}</Button>
            )}
            {step.tips.map((tip) => <p key={tip} className="rounded-md bg-warning-bg px-3 py-2 text-xs text-warning">{tip}</p>)}
            <p className="text-xs text-muted">Deu certo quando: {step.success}{step.seen ? ` Agora: ${step.seen}.` : ""}</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { trackAction("guide.overlay_check"); void q.refetch(); }}>Já fiz, conferir</Button>
              <Button size="sm" variant="ghost" onClick={() => setDelegating(step)}>Não tenho esse acesso</Button>
              <Button size="sm" variant="ghost" onClick={() => act(`/api/app/guide/steps/${step.key}/skip`)}>Pular</Button>
              <Button size="sm" variant="ghost" onClick={() => openAssistant(step.key)}><Sparkles className="h-3.5 w-3.5" aria-hidden /> Dúvida</Button>
            </div>
            <Link to="/app/guide" className="text-xs text-brand hover:underline">Ver todos os passos</Link>
          </div>
        )}
      </aside>
      {delegating && <DelegateDialog step={delegating} members={g.members} onClose={() => setDelegating(null)} />}
    </>
  );
}
